// src/pages/Home.js
import React, { useMemo, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { useParams, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { useData } from "./Cache";
import defaultImage from "../assets/field.jpg";
import logo from "../assets/elitLig_logo.png";
import "./Home.css";

function normalize(str = "") {
  return str
    .replace(/İ/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase("tr-TR")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ü/g, "u")
    .replace(/[^a-z0-9 ]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

export default function Home() {
  const { cityName, leagueName, seasonName } = useParams();
  const navigate = useNavigate();
  const { matches, teams, players, playerStatistics1, loading, error } = useData();

  // 1) Sezon maçları
  const seasonMatches = useMemo(
    () =>
      matches.filter(
        (m) =>
          normalize(m.city) === cityName &&
          normalize(m.league_name) === leagueName &&
          normalize(m.match_season) === seasonName
      ),
    [matches, cityName, leagueName, seasonName]
  );

  // helper
  const groupByDate = (arr) =>
    Object.entries(
      arr.reduce((acc, m) => {
        const d = m.date.split("T")[0];
        (acc[d] = acc[d] || []).push(m);
        return acc;
      }, {})
    );

  // 2) Carousel için: yalnızca sonuç maçları, yeni→eski, ilk 10
  const filteredMatches = useMemo(
    () =>
      seasonMatches
        .filter((m) => !m.is_it_fixture)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 10),
    [seasonMatches]
  );
  const [carouselIndex, setCarouselIndex] = useState(0);
  const currentMatch = filteredMatches[carouselIndex] || {};

  const prevMatch = () =>
    setCarouselIndex((i) => (i - 1 + filteredMatches.length) % filteredMatches.length);
  const nextMatch = () => setCarouselIndex((i) => (i + 1) % filteredMatches.length);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: nextMatch,
    onSwipedRight: prevMatch,
    trackMouse: true,
    preventDefaultTouchmoveEvent: true,
  });

  // 3) Fikstür: eski→yeni, ilk 10
  const fixturesByDate = useMemo(
    () =>
      groupByDate(
        seasonMatches
          .filter((m) => m.is_it_fixture)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 10)
      ),
    [seasonMatches]
  );

  // 4) Sonuçlar: eski→yeni, ilk 10
  const resultsByDate = useMemo(
    () =>
      groupByDate(
        seasonMatches
          .filter((m) => !m.is_it_fixture)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 10)
      ),
    [seasonMatches]
  );

  // 5) Puan Durumu (ilk 21 takım)
  const standings = useMemo(() => {
    const table = {};
    seasonMatches
      .filter((m) => !m.is_it_fixture)
      .forEach((m) => {
        const { first_team_name: H, second_team_name: A, first_team_score: HS, second_team_score: AS } = m;
        if (!table[H]) table[H] = { name: H, played: 0, wins: 0, draws: 0, losses: 0, scored: 0, conceded: 0, points: 0 };
        if (!table[A]) table[A] = { name: A, played: 0, wins: 0, draws: 0, losses: 0, scored: 0, conceded: 0, points: 0 };
        table[H].played++; table[A].played++;
        table[H].scored += HS; table[H].conceded += AS;
        table[A].scored += AS; table[A].conceded += HS;
        if (HS > AS) {
          table[H].wins++; table[H].points += 3; table[A].losses++;
        } else if (HS < AS) {
          table[A].wins++; table[A].points += 3; table[H].losses++;
        } else {
          table[H].draws++; table[A].draws++;
          table[H].points++; table[A].points++;
        }
      });
    return Object.values(table)
      .sort((a, b) =>
        b.points !== a.points
          ? b.points - a.points
          : (b.scored - b.conceded) - (a.scored - a.conceded)
      )
      .slice(0, 21);
  }, [seasonMatches]);

  // 6) Oyuncu istatistikleri (sezon bazlı)
  const getPlayerStats = (mode = "goals") => {
    const matchIds = new Set(seasonMatches.map((m) => m.id));
    const acc = {};
    playerStatistics1
      .filter((s) => matchIds.has(s.match_id))
      .forEach((s) => {
        if (!acc[s.player_id]) {
          const pl = players.find((p) => p.id === s.player_id);
          acc[s.player_id] = {
            id: pl.id,
            player_name: pl.player_name,
            team_name: pl.team_name,
            player_img: pl.player_img,
            total_matches: 0,
            total_goals: 0,
            total_points: 0,
            total_cards: 0,
          };
        }
        const o = acc[s.player_id];
        o.total_matches++;
        o.total_goals += s.number_of_goals || 0;
        o.total_points += s.points || 0;
        o.total_cards += (s.yellow_card || 0) + (s.red_card || 0);
      });
    return Object.values(acc)
      .map((p) => ({
        ...p,
        average_goals: (p.total_goals / p.total_matches).toFixed(2),
        value: Math.round((p.total_points / p.total_matches) * 100 + p.total_matches * 20 - p.total_cards * 30),
      }))
      .sort((a, b) =>
        mode === "goals"
          ? b.total_goals - a.total_goals || b.total_matches - a.total_matches
          : b.value - a.value || b.total_goals - a.total_goals
      );
  };
  const topScorers = useMemo(() => getPlayerStats("goals"), [seasonMatches]);
  const topValues = useMemo(() => getPlayerStats("value"), [seasonMatches]);

  if (loading) return <p className="home-loading">Yükleniyor…</p>;
  if (error) return <p className="home-error">Hata: {error}</p>;

  const fmtTime = (t = "") => t.slice(0, 5);
  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" });
  const getLogo = (team) => teams.find((t) => t.team_name === team)?.logo || logo;

  return (
    <>
      <Header />

      {/* SLAYT */}
<div className="hero-carousel-wrapper" {...swipeHandlers}>
  {filteredMatches.length === 0 ? (
    <div className="image-gallery">
      <div className="image-container">
        <img src={defaultImage} alt="Default" />
        <p className="default-message">Maç bulunamadı</p>
      </div>
    </div>
  ) : (
    <div className="image-gallery">
      <div className="image-container">
        <img
          src={currentMatch.match_picture || defaultImage}
          alt={currentMatch.match_title || "Match"}
        />

        {/* 1) Eğer başlık varsa göster */}
        {currentMatch.match_title ? (
          <button
            className="slide-caption"
            onClick={() =>
              navigate(`/${cityName}/${leagueName}/${seasonName}/maclar/${currentMatch.id}`)
            }
          >
            {currentMatch.match_title}
          </button>
        ) : (
          /* 2) Başlık yoksa büyük mini bilgi bloğu */
          <div className="slide-no-title">
            <div className="team-mini large">
              <img src={getLogo(currentMatch.first_team_name)} alt="" />
              <span>{currentMatch.first_team_name}</span>
              <strong className="score">{currentMatch.first_team_score}</strong>
            </div>
            <div className="team-mini large">
              <img src={getLogo(currentMatch.second_team_name)} alt="" />
              <span>{currentMatch.second_team_name}</span>
              <strong className="score">{currentMatch.second_team_score}</strong>
            </div>
          </div>
        )}

        {/* 3) Her iki durumda da altta küçük mini bilgi */}
        <div className="slide-info">
          <div className="team-mini">
            <img src={getLogo(currentMatch.first_team_name)} alt="" />
            <span>{currentMatch.first_team_name}</span>
          </div>
          <div className="score-mini">
            {currentMatch.first_team_score} – {currentMatch.second_team_score}
          </div>
          <div className="team-mini right">
            <span>{currentMatch.second_team_name}</span>
            <img src={getLogo(currentMatch.second_team_name)} alt="" />
          </div>
        </div>

        {/* 4) Oklar */}
        <div className="button-container-home">
          <button className="nav-button prev" onClick={prevMatch}>‹</button>
          <button className="nav-button next" onClick={nextMatch}>›</button>
        </div>
      </div>
    </div>
  )}
</div>

      <div className="home-container">
        {/* FİKSTÜR */}
        <section className="home-section">
          <h2 className="home-header">Fikstür</h2>
          {fixturesByDate.map(([date, games]) => (
            <div key={date} className="date-group">
              <h3 className="date-title">{fmtDate(date)}</h3>
              <table className="home-table">
                <tbody>
                  {games.map((m) => (
                    <tr
                      key={m.id}
                      className="match-row"
                      onClick={() =>
                        navigate(`/${cityName}/${leagueName}/${seasonName}/maclar/${m.id}`)
                      }
                    >
                      <td className="team-cell">
                        <img src={getLogo(m.first_team_name)} className="team-logo" alt="" />
                        {m.first_team_name}
                      </td>
                      <td className="time-cell">{fmtTime(m.time)}</td>
                      <td className="team-cell away">
                        {m.second_team_name}
                        <img src={getLogo(m.second_team_name)} className="team-logo" alt="" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>

        {/* SONUÇLAR */}
        <section className="home-section">
          <h2 className="home-header">Sonuçlar</h2>
          {resultsByDate.map(([date, games]) => (
            <div key={date} className="date-group">
              <h3 className="date-title">{fmtDate(date)}</h3>
              <table className="home-table">
                <tbody>
                  {games.map((m) => (
                    <tr
                      key={m.id}
                      className="match-row"
                      onClick={() =>
                        navigate(`/${cityName}/${leagueName}/${seasonName}/maclar/${m.id}`)
                      }
                    >
                      <td className="team-cell">
                        <img src={getLogo(m.first_team_name)} className="team-logo" alt="" />
                        {m.first_team_name}
                      </td>
                      <td className="score-cell">
                        {m.first_team_score} - {m.second_team_score}
                      </td>
                      <td className="team-cell away">
                        {m.second_team_name}
                        <img src={getLogo(m.second_team_name)} className="team-logo" alt="" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </section>

        {/* PUAN DURUMU */}
        <section className="home-section">
          <h2 className="home-header">Puan Durumu</h2>
          <table className="home-table">
            <thead>
              <tr><th>#</th><th>Takım</th><th>O</th><th>G</th><th>B</th><th>M</th><th>Av</th><th>P</th></tr>
            </thead>
            <tbody>
              {standings.map((t, i) => (
                <tr
                  key={t.name}
                  className="standing-row"
                  onClick={() => navigate(`/${cityName}/takimlar/${normalize(t.name)}`)}
                >
                  <td>{i + 1}</td>
                  <td className="team-cell">
                    <img src={getLogo(t.name)} className="team-logo" alt="" />
                    {t.name}
                  </td>
                  <td>{t.played}</td>
                  <td>{t.wins}</td>
                  <td>{t.draws}</td>
                  <td>{t.losses}</td>
                  <td>{t.scored - t.conceded}</td>
                  <td>{t.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* OYUNCU İSTATİSTİKLERİ */}
        <section className="home-section">
          {/* Gol Krallığı */}
          <div className="stats-block">
            <h2 className="home-header">Gol Krallığı</h2>
            <div
              className="stats-card clickable-card"
              onClick={() => navigate(`/${cityName}/oyuncular/${topScorers[0]?.id}`)}
            >
              <div className="stats-card-info">
                <div className="stat-item"><span>Maç</span><strong>{topScorers[0]?.total_matches}</strong></div>
                <div className="stat-item"><span>Gol</span><strong>{topScorers[0]?.total_goals}</strong></div>
                <div className="stat-item"><span>M.B.Gol</span><strong>{topScorers[0]?.average_goals}</strong></div>
                <div className="stat-item"><span>Değer</span><strong>{topScorers[0]?.value}</strong></div>
              </div>
              <div className="stats-card-img"><img src={topScorers[0]?.player_img} alt={topScorers[0]?.player_name} /></div>
              <div className="stats-card-footer">
                <strong>{topScorers[0]?.player_name}</strong>
                <span>{topScorers[0]?.team_name}</span>
              </div>
            </div>
            <table className="home-table stats-table">
              <thead><tr><th>#</th><th>Foto</th><th>İsim</th><th>Goller</th></tr></thead>
              <tbody>
                {topScorers.slice(1,10).map((p,idx)=>(
                  <tr key={p.id} className="clickable-row" onClick={()=>navigate(`/${cityName}/oyuncular/${p.id}`)}>
                    <td>{idx+2}</td>
                    <td><img src={p.player_img} className="player-thumb" alt={p.player_name} /></td>
                    <td className="player-name-cell">
                      <strong>{p.player_name}</strong>
                      <span className="player-team">{p.team_name}</span>
                    </td>
                    <td><strong>{p.total_goals}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* En Değerliler */}
          <div className="stats-block">
            <h2 className="home-header">En Değerliler</h2>
            <div
              className="stats-card clickable-card"
              onClick={() => navigate(`/${cityName}/oyuncular/${topValues[0]?.id}`)}
            >
              <div className="stats-card-info">
                <div className="stat-item"><span>Maç</span><strong>{topValues[0]?.total_matches}</strong></div>
                <div className="stat-item"><span>Gol</span><strong>{topValues[0]?.total_goals}</strong></div>
                <div className="stat-item"><span>M.B.Puan</span><strong>{(topValues[0].total_points/topValues[0].total_matches).toFixed(2)}</strong></div>
                <div className="stat-item"><span>Değer</span><strong>{topValues[0]?.value}</strong></div>
              </div>
              <div className="stats-card-img"><img src={topValues[0]?.player_img} alt={topValues[0]?.player_name} /></div>
              <div className="stats-card-footer">
                <strong>{topValues[0]?.player_name}</strong>
                <span>{topValues[0]?.team_name}</span>
              </div>
            </div>
            <table className="home-table stats-table">
              <thead><tr><th>#</th><th>Foto</th><th>İsim</th><th>Değer</th></tr></thead>
              <tbody>
                {topValues.slice(1,10).map((p,idx)=>(
                  <tr key={p.id} className="clickable-row" onClick={()=>navigate(`/${cityName}/oyuncular/${p.id}`)}>
                    <td>{idx+2}</td>
                    <td><img src={p.player_img} className="player-thumb" alt={p.player_name} /></td>
                    <td className="player-name-cell">
                      <strong>{p.player_name}</strong>
                      <span className="player-team">{p.team_name}</span>
                    </td>
                    <td><strong>{p.value}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}
